import { useEffect, useMemo, useRef } from "react";
import { Typography, Box, Stack, Chip, useTheme, alpha } from "@mui/material";
import { useResponsive } from "../../../core/hooks/useResponsive";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Bar,
  ComposedChart,
  Customized,
} from "recharts";

function formatInr(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(n));
}

/**
 * OHLC candles with standard colouring: green when the day closed at or above its own open,
 * red when it closed below. Your buy price is the dashed reference line, independent of colour.
 */
function CandlestickLayer({ xAxisMap, yAxisMap, chartData, profit, loss, maxCandleWidth }) {
  if (!chartData?.length) return null;
  const xAxis = xAxisMap && Object.values(xAxisMap)[0];
  const yAxis = yAxisMap && Object.values(yAxisMap)[0];
  const xScale = xAxis?.scale;
  const yScale = yAxis?.scale;
  if (!xScale || !yScale) return null;
  const bw = typeof xScale.bandwidth === "function" ? xScale.bandwidth() : 8;
  // A month of daily bars is only ~22 candles, so they can be drawn properly wide instead of
  // the hairlines the old unbounded-range chart needed.
  const candleW = Math.min(maxCandleWidth, Math.max(5, bw * 0.62));

  return (
    <g className="ohlc-candles">
      {chartData.map((d, i) => {
        const xStart = xScale(d.label);
        const center = typeof xScale.bandwidth === "function" ? xStart + xScale.bandwidth() / 2 : xStart;
        const yHigh = yScale(d.high);
        const yLow = yScale(d.low);
        const yOpen = yScale(d.open);
        const yClose = yScale(d.close);
        const o = Number(d.open);
        const c = Number(d.close);
        const isGreen = c >= o;
        const color = isGreen ? profit : loss;
        const bodyTop = Math.min(yOpen, yClose);
        const bodyBot = Math.max(yOpen, yClose);
        const bodyH = Math.max(bodyBot - bodyTop, 1);
        return (
          <g key={`candle-${d.idx ?? i}`}>
            <line x1={center} y1={yHigh} x2={center} y2={yLow} stroke={color} strokeWidth={1.25} />
            <rect
              x={center - candleW / 2}
              y={bodyTop}
              width={candleW}
              height={bodyH}
              fill={color}
              fillOpacity={0.9}
              stroke={color}
              strokeWidth={1}
            />
          </g>
        );
      })}
    </g>
  );
}

export const ProfitLossHistory = ({ history, months, buyPrice }) => {
  const { isPhone } = useResponsive();
  const theme = useTheme();
  const profit = theme.palette.success?.main || "#2e7d32";
  const loss = theme.palette.error?.main || "#c62828";
  const primary = theme.palette.primary.main;

  const buy = Number(buyPrice);
  const hasBuy = Number.isFinite(buy);
  const windowLabel = months ? `${months}M` : "1M";

  const chartData = useMemo(() => {
    if (!history?.length) return [];
    return history.map((row, i) => ({
      ...row,
      idx: i,
      label:
        typeof row.date === "string" && row.date.length >= 10
          ? row.date.slice(5, 10).replace("-", "/")
          : String(i + 1),
      open: Number(row.open),
      high: Number(row.high),
      low: Number(row.low),
      close: Number(row.close),
    }));
  }, [history]);

  /**
   * The buy line has to stay on screen even when price has run far from it, otherwise the
   * "above or below my buy" read is lost — so it is folded into the domain, not just drawn.
   */
  const yDomain = useMemo(() => {
    if (!chartData.length) return ["auto", "auto"];
    let lo = Math.min(...chartData.map((d) => d.low));
    let hi = Math.max(...chartData.map((d) => d.high));
    if (hasBuy) {
      lo = Math.min(lo, buy);
      hi = Math.max(hi, buy);
    }
    const pad = Math.max((hi - lo) * 0.06, hi * 0.004, 0.05);
    return [Number((lo - pad).toFixed(2)), Number((hi + pad).toFixed(2))];
  }, [chartData, buy, hasBuy]);

  // The plot is sized per bar and scrolled rather than squeezed into the container, so a quarter
  // of daily candles stays readable instead of collapsing into hairlines.
  const pxPerBar = isPhone ? 14 : 17;
  const plotWidth = Math.max(240, chartData.length * pxPerBar);

  // One date label per ~70px of plot, which is independent of how wide the container happens
  // to be now that the plot scrolls.
  const tickInterval = Math.max(0, Math.ceil(70 / pxPerBar) - 1);

  const scrollRef = useRef(null);
  // Open on the most recent sessions - that is what a holder looks at first - and scroll left
  // for history. It also keeps the right-hand price axis in view by default.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [chartData.length, plotWidth]);
  const candleTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    const c = Number(p?.close);
    const vsBuy = hasBuy && Number.isFinite(c) ? c - buy : null;
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          border: `1px solid ${alpha(primary, 0.2)}`,
          borderRadius: 1,
          px: 1.25,
          py: 0.75,
          boxShadow: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
          {p?.date || p?.label}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          O {formatInr(p?.open)} · H {formatInr(p?.high)}
        </Typography>
        <Typography variant="body2">
          L {formatInr(p?.low)} · C {formatInr(p?.close)}
        </Typography>
        {vsBuy != null && (
          <Typography
            variant="caption"
            fontWeight={800}
            sx={{ display: "block", mt: 0.5, color: vsBuy >= 0 ? profit : loss }}
          >
            {vsBuy >= 0 ? "+" : ""}
            {formatInr(vsBuy)} vs your buy
          </Typography>
        )}
      </Box>
    );
  };

  if (!history || history.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
        No price history available
      </Typography>
    );
  }


  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.04em" }}>
          DAILY PRICE · LAST {windowLabel} ({chartData.length} SESSIONS)
        </Typography>
        {hasBuy && (
          <Chip
            size="small"
            label={`Buy ${formatInr(buy)}`}
            sx={{
              fontWeight: 800,
              bgcolor: alpha(primary, 0.12),
              color: primary,
              border: `1px dashed ${alpha(primary, 0.5)}`,
            }}
          />
        )}
      </Stack>

      <Box
        sx={{
          width: "100%",
          // Taller than the old toggle-based chart: the toggle row is gone and a month of
          // candles needs vertical room for the body/wick split to be readable.
          height: { xs: 260, sm: 310, md: 360 },
          borderRadius: 2,
          border: `1px solid ${alpha(primary, 0.1)}`,
          overflowX: "auto",
          overflowY: "hidden",
          mb: 1,
          bgcolor: alpha(primary, 0.02),
          // A default-width scrollbar eats into the plot height; this keeps it slim but visible
          // so it is obvious there is more history to the left.
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { borderRadius: 4, background: alpha(primary, 0.35) },
          scrollbarWidth: "thin",
        }}
        ref={scrollRef}
      >
        <Box sx={{ minWidth: `${plotWidth}px`, width: "100%", height: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 22, right: 4, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.9)} vertical={false} />
            <XAxis
              dataKey="label"
              interval={tickInterval}
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
              minTickGap={2}
            />
            <YAxis
              // Right-hand price scale, as trading charts conventionally use - and because the
              // plot opens scrolled to the latest session, it keeps the scale in view by default.
              orientation="right"
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
              width={isPhone ? 46 : 56}
              domain={yDomain}
              allowDecimals
              tickFormatter={(v) => {
                const n = Number(v);
                if (!Number.isFinite(n)) return "";
                const a = Math.abs(n);
                if (a >= 10000) return `${(n / 1000).toFixed(1)}k`;
                return a >= 100 ? n.toFixed(0) : n.toFixed(1);
              }}
            />
            <Tooltip content={candleTooltip} cursor={{ strokeDasharray: "3 3" }} />
            {hasBuy && (
              <ReferenceLine
                y={buy}
                stroke={primary}
                strokeDasharray="6 4"
                strokeWidth={2}
                ifOverflow="extendDomain"
                label={{
                  value: `Buy ${buy}`,
                  fill: primary,
                  fontSize: 11,
                  fontWeight: 700,
                  position: "insideTopLeft",
                }}
              />
            )}
            <Bar dataKey="high" fill="transparent" stroke="none" isAnimationActive={false} />
            <Customized
              component={(cProps) => (
                <CandlestickLayer
                  {...cProps}
                  chartData={chartData}
                  profit={profit}
                  loss={loss}
                  maxCandleWidth={isPhone ? 12 : 18}
                />
              )}
            />
          </ComposedChart>
        </ResponsiveContainer>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Scroll left for earlier sessions. Green closed at or above its open, red below it. The dashed line is your buy price —
        candles sitting above it are in profit.
      </Typography>

    </Box>
  );
};
